import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { documentsV2Api, DocumentTemplateV2ListItem } from "api/documentsV2Api";
import { useDocumentTemplatesV2 } from "api/hooks";
import { PaziaButton, PaziaLoader } from "components/atoms";
import {
  AdminSearchInput,
  FiltersBackground,
  PageMain,
  PaziaDeleteModal,
  TanTableClient,
  TanTableColumn,
} from "components/molecules";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { routes } from "consts";
import { formatDate, getApiErrorMessage, useToaster } from "utils";

/** "Dokumenty 2.0": the list of pdfme templates with their usage. Editing happens on the editor page. */
export const DocumentsV2Page = (): JSX.Element => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToaster();
  const [search, setSearch] = useState("");
  const { isLoading, templates, invalidate } = useDocumentTemplatesV2(search);
  // Local state on purpose: the shared delete context would open every delete modal mounted on the screen.
  const [templateToDelete, setTemplateToDelete] = useState<DocumentTemplateV2ListItem | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);

  const edit = (template: DocumentTemplateV2ListItem) => navigate(routes.DOCUMENTS_V2_EDITOR(template.id));

  const duplicate = async (template: DocumentTemplateV2ListItem) => {
    setDuplicatingId(template.id);
    try {
      await documentsV2Api.duplicate(template.id);
      await invalidate();
      showSuccess(`Zduplikowano szablon „${template.name}”`);
    } catch (error) {
      showError(getApiErrorMessage(error, "Nie udało się zduplikować szablonu"));
    } finally {
      setDuplicatingId(null);
    }
  };

  const confirmDelete = async () => {
    const template = templateToDelete;
    setTemplateToDelete(null);
    if (!template) return;
    try {
      await documentsV2Api.remove(template.id);
      await invalidate();
      showSuccess(`Usunięto szablon „${template.name}”`);
    } catch (error) {
      // A template used by course documents is refused with a Polish message that names the courses.
      showError(getApiErrorMessage(error, "Nie udało się usunąć szablonu"));
    }
  };

  const columns = useMemo<TanTableColumn<DocumentTemplateV2ListItem>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Nazwa",
        enableColumnFilter: false,
      },
      {
        id: "type",
        accessorFn: (template) => template.type?.name ?? "",
        header: "Typ",
        enableColumnFilter: false,
      },
      {
        id: "lastModified",
        accessorFn: (template) => (template.lastModified ? new Date(template.lastModified) : null),
        header: "Ostatnia zmiana",
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue<Date | null>();
          return value && !isNaN(value.getTime()) ? formatDate(value, true) : "";
        },
      },
      {
        id: "courseDocumentCount",
        accessorKey: "courseDocumentCount",
        header: "Używany w kursach",
        enableColumnFilter: false,
      },
      {
        id: "actions",
        header: "Akcje",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const template = info.row.original;
          return (
            <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
              <PaziaButton type="tertiary" btnType="button" title="Edytuj" onClick={() => edit(template)} />
              <PaziaButton
                type="tertiary"
                btnType="button"
                title="Duplikuj"
                disabled={duplicatingId !== null}
                processing={duplicatingId === template.id}
                onClick={() => duplicate(template)}
              />
              <PaziaButton
                type="danger2"
                btnType="button"
                title="Usuń"
                onClick={() => setTemplateToDelete(template)}
              />
            </div>
          );
        },
      },
    ],
    // `edit` and `duplicate` only close over stable values (navigate, toaster, the query client).
    [duplicatingId]
  );

  return (
    <PageMain>
      <PageRichHeader header="Szablony dokumentów 2.0">
        <div className="flex grow justify-between">
          <div>
            <PaziaButton
              type="primary"
              btnType="button"
              title="Dodaj +"
              onClick={() => navigate(routes.DOCUMENTS_V2_ADD)}
            />
          </div>
          <div>
            <PaziaButton
              type="tertiary"
              btnType="button"
              title="Edytuj typy dokumentów"
              onClick={() => navigate(routes.DOCUMENT_TYPES)}
            />
          </div>
        </div>
      </PageRichHeader>

      <FiltersBackground className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <AdminSearchInput onSearch={setSearch} className="self-end" />
        </div>
      </FiltersBackground>

      {isLoading ? (
        <PaziaLoader position="center" />
      ) : (
        <TanTableClient
          data={templates}
          columns={columns}
          initialSorting={[{ id: "lastModified", desc: true }]}
          onRowClick={(template) => edit(template as DocumentTemplateV2ListItem)}
        />
      )}

      <PaziaDeleteModal
        id="delete-document-template-v2"
        isOpen={templateToDelete !== null}
        title="Usuń szablon"
        description={`Czy na pewno chcesz usunąć szablon „${templateToDelete?.name ?? ""}”? Tej operacji nie można cofnąć.`}
        onCloseRequest={() => setTemplateToDelete(null)}
        onConfirm={confirmDelete}
      />
    </PageMain>
  );
};
